/**
 * Sync Progress Indicator Component
 * Shows real-time sync progress for network synchronization
 */

import React from 'react';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import type { SyncProgress } from '../../types/networks.types';

interface SyncProgressIndicatorProps {
  progress: SyncProgress;
  variant?: 'inline' | 'toast';
  className?: string;
}

export const SyncProgressIndicator: React.FC<SyncProgressIndicatorProps> = ({
  progress,
  variant = 'inline',
  className = ''
}) => {
  const getStatusColor = () => {
    switch (progress.status) {
      case 'syncing': return 'bg-blue-500';
      case 'success': return 'bg-green-500';
      case 'error': return 'bg-red-500';
      default: return 'bg-gray-300';
    }
  };

  const getStatusIcon = () => {
    switch (progress.status) {
      case 'syncing': return <Loader2 className="w-4 h-4 animate-spin" />;
      case 'success': return <CheckCircle className="w-4 h-4" />;
      case 'error': return <XCircle className="w-4 h-4" />;
      default: return null;
    }
  };

  const getStatusText = () => {
    switch (progress.status) {
      case 'syncing': return 'Syncing...';
      case 'success': return 'Sync Complete';
      case 'error': return 'Sync Failed';
      default: return 'Idle';
    }
  };

  if (variant === 'toast') {
    return (
      <div className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 p-4 min-w-[300px] ${className}`}>
        <div className="flex items-start gap-3">
          <div className={`flex-shrink-0 text-white ${progress.status === 'syncing' ? 'text-blue-600' : progress.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
            {getStatusIcon()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {getStatusText()}
              </p>
              {progress.status === 'syncing' && (
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {progress.progress}%
                </span>
              )}
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {progress.message}
            </p>
            {progress.status === 'syncing' && (
              <div className="mt-2">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all duration-300 ${getStatusColor()}`}
                    style={{ width: `${progress.progress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inline variant
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className={`flex-shrink-0 ${progress.status === 'syncing' ? 'text-blue-600' : progress.status === 'success' ? 'text-green-600' : 'text-red-600'}`}>
        {getStatusIcon()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {getStatusText()}
          </p>
          {progress.status === 'syncing' && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {progress.progress}%
            </span>
          )}
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">
          {progress.message}
        </p>
        {progress.status === 'syncing' && (
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1">
            <div
              className={`h-1 rounded-full transition-all duration-300 ${getStatusColor()}`}
              style={{ width: `${progress.progress}%` }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
